/**
 * ProgressTracker Component
 * Displays action items and progress from project documents.
 */

import React from 'react';
import { ListChecks } from 'lucide-react';
import type { ProgressItem } from '@/services/projectAnalysisApi';
import { InsightCard } from './InsightCard';

interface ProgressTrackerProps {
  items: ProgressItem[];
}

export const ProgressTracker: React.FC<ProgressTrackerProps> = ({ items }) => {
  if (items.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <ListChecks className="w-12 h-12 mx-auto mb-3 text-slate-300" />
        <p>No action items found in your documents.</p>
        <p className="text-sm mt-1">Add meeting notes with action items to track progress.</p>
      </div>
    );
  }

  // Group by status
  const grouped = {
    'in-progress': items.filter(i => i.status === 'in-progress'),
    blocked: items.filter(i => i.status === 'blocked'),
    planned: items.filter(i => i.status === 'planned'),
    completed: items.filter(i => i.status === 'completed'),
  };

  return (
    <div className="space-y-6">
      {/* In Progress */}
      {grouped['in-progress'].length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-blue-600 mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-blue-600" />
            In Progress ({grouped['in-progress'].length})
          </h3>
          <div className="space-y-3">
            {grouped['in-progress'].map((item, index) => (
              <InsightCard
                key={`progress-${index}`}
                type="progress"
                title={item.item}
                source={item.source}
                status={item.status}
                owner={item.owner}
              />
            ))}
          </div>
        </div>
      )}

      {/* Blocked */}
      {grouped.blocked.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-red-600 mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-600" />
            Blocked ({grouped.blocked.length})
          </h3>
          <div className="space-y-3">
            {grouped.blocked.map((item, index) => (
              <InsightCard
                key={`blocked-${index}`}
                type="progress"
                title={item.item}
                source={item.source}
                status={item.status}
                owner={item.owner}
              />
            ))}
          </div>
        </div>
      )}

      {/* Planned */}
      {grouped.planned.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-slate-600 mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-slate-400" />
            Planned ({grouped.planned.length})
          </h3>
          <div className="space-y-3">
            {grouped.planned.map((item, index) => (
              <InsightCard
                key={`planned-${index}`}
                type="progress"
                title={item.item}
                source={item.source}
                status={item.status}
                owner={item.owner}
              />
            ))}
          </div>
        </div>
      )}

      {/* Completed */}
      {grouped.completed.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-emerald-600 mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-600" />
            Completed ({grouped.completed.length})
          </h3>
          <div className="space-y-3">
            {grouped.completed.map((item, index) => (
              <InsightCard
                key={`completed-${index}`}
                type="progress"
                title={item.item}
                source={item.source}
                status={item.status}
                owner={item.owner}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressTracker;
